import { createHttpClient } from '@/infrastructure/http/ofetchClient';
import { HttpAuthGateway } from '@/infrastructure/http/HttpAuthGateway';
import { HttpTenantFeatureGateway } from '@/infrastructure/http/HttpTenantFeatureGateway';
import { HttpIesGateway } from '@/infrastructure/http/HttpIesGateway';
import { HttpEngajamentoGateway } from '@/infrastructure/http/HttpEngajamentoGateway';
import { LocalSessionStorage } from '@/infrastructure/storage/LocalSessionStorage';
import {
  ForgotPasswordUseCaseImpl,
  LoginUseCaseImpl,
  LogoutUseCaseImpl,
  ResetPasswordUseCaseImpl,
} from '@/application/usecase/LoginUseCaseImpl';
import {
  ListTenantFeaturesUseCaseImpl,
  ToggleFeatureUseCaseImpl,
  ApplyProfileUseCaseImpl,
} from '@/application/usecase/TenantFeatureUseCaseImpl';
import { GetMatriculaDisciplines } from '@/application/usecase/GetMatriculaDisciplines';
import type { GetMatriculaDisciplinesUseCase } from '@/application/port/in/GetMatriculaDisciplinesUseCase';
import { GetEngajamento } from '@/application/usecase/GetEngajamento';
import type { GetEngajamentoUseCase } from '@/application/port/in/GetEngajamentoUseCase';
import type { EngajamentoGateway } from '@/application/port/out/EngajamentoGateway';
import type { VerificarContatoAlunoUseCase } from '@/application/port/in/VerificarContatoAlunoUseCase';
import type { GetFinanceiroDashboardUseCase } from '@/application/port/in/GetFinanceiroDashboardUseCase';
import type {
  ListPagamentosByParcelaUseCase, RegistrarPagamentoManualUseCase,
} from '@/application/port/in/PagamentoUseCase';
import type {
  ListCobrancaReguaPassosUseCase, CreateCobrancaReguaPassoUseCase,
  UpdateCobrancaReguaPassoUseCase, DeleteCobrancaReguaPassoUseCase, RunReguaCobrancaUseCase,
} from '@/application/port/in/CobrancaReguaUseCase';
import type {
  ListRenegociacoesUseCase, SolicitarRenegociacaoUseCase, DecidirRenegociacaoUseCase,
} from '@/application/port/in/RenegociacaoUseCase';
import type {
  ListEstornosUseCase, SolicitarEstornoUseCase, DecidirEstornoUseCase,
  ProcessarEstornoGatewayUseCase,
} from '@/application/port/in/EstornoUseCase';
import type {
  ListVendasUseCase, GetVendaUseCase, ListVendaParcelasUseCase,
  ListVendaDescontosUseCase, CancelarVendaUseCase,
} from '@/application/port/in/VendaUseCase';
import type { ListAdminParcelasUseCase } from '@/application/port/in/AdminParcelaUseCase';
import type { ListAdminPagamentosUseCase } from '@/application/port/in/AdminPagamentoUseCase';
import type {
  ListCobrancaEnviosUseCase, ListCobrancaEnviosByParcelaUseCase,
} from '@/application/port/in/CobrancaEnvioUseCase';
import type {
  ListCommissionRulesUseCase, GetCommissionRuleUseCase,
  CriarCommissionRuleUseCase, AtualizarCommissionRuleUseCase, DesativarCommissionRuleUseCase,
  ListCommissionEntriesUseCase, ApropriarCommissionEntryUseCase, PagarCommissionEntryUseCase,
} from '@/application/port/in/CommissionUseCase';
import type {
  ListPrecosUseCase, GetPrecoUseCase, CriarPrecoUseCase, AtualizarPrecoUseCase, InativarPrecoUseCase,
  ListPrecoMeiosUseCase, AddPrecoMeioUseCase, RemovePrecoMeioUseCase,
} from '@/application/port/in/PrecoUseCase';
import type {
  ListDescontosUseCase, GetDescontoUseCase, CriarDescontoUseCase,
  AtivarDescontoUseCase, DesativarDescontoUseCase,
} from '@/application/port/in/DescontoUseCase';
import type {
  ListWebhookSubscriptionsUseCase, GetWebhookSubscriptionUseCase,
  CriarWebhookSubscriptionUseCase, AtualizarWebhookSubscriptionUseCase, DesativarWebhookSubscriptionUseCase,
  ListWebhookDeliveriesUseCase, ReplayWebhookDeliveryUseCase,
  ListMetasVendasUseCase, GetMetaVendasUseCase,
  CriarMetaVendasUseCase, AtualizarMetaVendasUseCase, DesativarMetaVendasUseCase,
  ListAlertasVendaUseCase, GetAlertaVendaUseCase,
  DeferirAlertaVendaUseCase, IndeferirAlertaVendaUseCase,
} from '@/application/port/in/Camada4UseCase';
import type {
  GetTenantPagamentoConfigUseCase, UpsertTenantPagamentoConfigUseCase,
} from '@/application/port/in/TenantPagamentoConfigUseCase';
import { buildCommerceSlice } from '@/infrastructure/di/builders/commerceBuilder';
import { buildCrmSlice } from '@/infrastructure/di/builders/crmBuilder';
import { buildContentSlice } from '@/infrastructure/di/builders/contentBuilder';
import { buildOrganizationSlice } from '@/infrastructure/di/builders/organizationBuilder';
import { buildIdentitySlice } from '@/infrastructure/di/builders/identityBuilder';
import { buildEnrollmentAdminSlice } from '@/infrastructure/di/builders/enrollmentAdminBuilder';
import { buildCommerceGapsSlice } from '@/infrastructure/di/builders/commerceGapsBuilder';
import type {
  ListLeadsUseCase, GetLeadUseCase, MoverLeadStageUseCase, MarcarLeadPerdidoUseCase,
} from '@/application/port/in/LeadUseCase';
import type {
  ListPipelinesUseCase, ListPipelineStagesUseCase,
  CreatePipelineUseCase, UpdatePipelineUseCase, DeactivatePipelineUseCase,
} from '@/application/port/in/PipelineUseCase';
import type {
  ListOpportunitiesUseCase, GetOpportunityUseCase,
  CriarOpportunityUseCase, TransicionarOpportunityUseCase, ConverterOpportunityUseCase,
} from '@/application/port/in/OpportunityUseCase';
import type {
  ListMetodosAvaliativosUseCase, CreateMetodoAvaliativoUseCase,
  UpdateMetodoAvaliativoUseCase, DeleteMetodoAvaliativoUseCase,
} from '@/application/port/in/MetodoAvaliativoUseCase';
import type {
  ListPeriodosLetivosUseCase, CreatePeriodoLetivoUseCase, UpdatePeriodoLetivoUseCase,
  MarcarVigentePeriodoUseCase, EncerrarPeriodoUseCase,
} from '@/application/port/in/PeriodoLetivoUseCase';
import type {
  ListCalendarioEventosUseCase, CreateCalendarioEventoUseCase,
  UpdateCalendarioEventoUseCase, DeleteCalendarioEventoUseCase,
} from '@/application/port/in/CalendarioEventoUseCase';
import type {
  ListAvaliacoesUseCase, GetAvaliacaoUseCase, CreateAvaliacaoUseCase,
  UpdateAvaliacaoUseCase, PublishAvaliacaoUseCase, ArchiveAvaliacaoUseCase,
  DeleteAvaliacaoUseCase,
  ListAvaliacaoQuestoesUseCase, AddQuestaoToAvaliacaoUseCase,
  RemoveQuestaoFromAvaliacaoUseCase,
} from '@/application/port/in/AvaliacaoUseCase';
import type {
  ListQuestoesUseCase, GetQuestaoUseCase, CreateQuestaoUseCase,
  UpdateQuestaoUseCase, DeleteQuestaoUseCase,
} from '@/application/port/in/QuestaoUseCase';
import type {
  ListTentativasUseCase, GetTentativaUseCase,
} from '@/application/port/in/TentativaUseCase';
import type {
  ListDocentesUseCase, GetDocenteUseCase, CreateDocenteUseCase,
  UpdateDocenteUseCase, ArchiveDocenteUseCase,
  ListDocenteAssignmentsUseCase, AssignDocenteUseCase, UnassignDocenteUseCase,
} from '@/application/port/in/DocenteUseCase';
import { GetCurrentIesUseCaseImpl } from '@/application/usecase/IesUseCaseImpl';
import { buildAcademySlice } from '@/infrastructure/di/builders/academyBuilder';
import { buildAlunoSlice } from '@/infrastructure/di/builders/alunoBuilder';
import { buildPerfilAlunoSlice } from '@/infrastructure/di/builders/perfilAlunoBuilder';
import { buildAcadadminSlice } from '@/infrastructure/di/builders/acadadminBuilder';
import { buildAcadadminGapsSlice } from '@/infrastructure/di/builders/acadadminGapsBuilder';
import { buildAdminGaps2Slice } from '@/infrastructure/di/builders/adminGaps2Builder';
import { buildCatalogConfigSlice } from '@/infrastructure/di/builders/catalogConfigBuilder';
import { buildConteudoExternoSlice } from '@/infrastructure/di/builders/conteudoExternoBuilder';
import type {
  ListEducationLevelsUseCase, CreateEducationLevelUseCase, UpdateEducationLevelUseCase,
  ListKnowledgeAreasUseCase, CreateKnowledgeAreaUseCase, UpdateKnowledgeAreaUseCase,
  ListTitulacoesUseCase, CreateTitulacaoUseCase, UpdateTitulacaoUseCase,
  ListRegrasRematriculaUseCase, CreateRegraRematriculaUseCase, UpdateRegraRematriculaUseCase,
} from '@/application/port/in/CatalogConfigUseCase';
import type {
  ListContentProvidersUseCase, CreateContentProviderUseCase, UpdateContentProviderUseCase, ArchiveContentProviderUseCase,
  BrowseProviderCatalogUseCase,
  ListUnidadesUseCase, CreateUnidadeUseCase, UpdateUnidadeUseCase, ArchiveUnidadeUseCase,
  ListExternalContentsUseCase, CreateExternalContentUseCase, UpdateExternalContentUseCase, ArchiveExternalContentUseCase,
} from '@/application/port/in/ConteudoExternoUseCase';
import { buildPedagogicoSlice } from '@/infrastructure/di/builders/pedagogicoBuilder';
import type {
  ListAssignmentsUseCase, CreateAssignmentUseCase, UpdateAssignmentUseCase,
  PublishAssignmentUseCase, EncerrarAssignmentUseCase, ArquivarAssignmentUseCase,
  ListAssignmentSubmissionsUseCase, CorrigirSubmissionUseCase, DownloadSubmissionArquivoUseCase,
} from '@/application/port/in/AssignmentUseCase';
import type {
  ListForumTopicosUseCase, CreateForumTopicoUseCase, UpdateForumTopicoUseCase,
  EncerrarForumTopicoUseCase, ArquivarForumTopicoUseCase,
  ListForumRespostasUseCase, CorrigirForumRespostaUseCase,
} from '@/application/port/in/ForumUseCase';
import { buildTutoriaSlice } from '@/infrastructure/di/builders/tutoriaBuilder';
import type {
  ListTutoresUseCase, CreateTutorUseCase, UpdateTutorUseCase, ArquivarTutorUseCase,
  ListAssuntosUseCase, CreateAssuntoUseCase, UpdateAssuntoUseCase,
  ListSolicitacoesUseCase, AssumirSolicitacaoUseCase, ListSolicitacaoMensagensUseCase,
  ResponderSolicitacaoUseCase, EncerrarSolicitacaoUseCase,
} from '@/application/port/in/TutoriaUseCase';
import type { UploadCommunicationFileUseCase, DownloadCommunicationFileUseCase } from '@/application/port/in/CommunicationFileUseCase';
import { buildDisciplineCompositionSlice } from '@/infrastructure/di/builders/disciplineCompositionBuilder';
import type { ListDisciplineResourcesUseCase, MutateDisciplineResourceUseCase } from '@/application/port/in/DisciplineCompositionUseCase';
import { buildComunicacaoSlice } from '@/infrastructure/di/builders/comunicacaoBuilder';
import { buildEbookSlice } from '@/infrastructure/di/builders/ebookBuilder';
import type {
  ListEbooksUseCase, CreateEbookUseCase, UpdateEbookUseCase, ArquivarEbookUseCase, BaixarEbookUseCase,
} from '@/application/port/in/EbookUseCase';
import { buildVideoaulaSlice } from '@/infrastructure/di/builders/videoaulaBuilder';
import type {
  ListVideoaulasUseCase, CreateVideoaulaUseCase, UpdateVideoaulaUseCase, ArquivarVideoaulaUseCase,
} from '@/application/port/in/VideoaulaUseCase';
import { buildAmbientacaoSlice } from '@/infrastructure/di/builders/ambientacaoBuilder';
import type {
  ListAmbientacoesUseCase, CreateAmbientacaoUseCase, UpdateAmbientacaoUseCase, ArquivarAmbientacaoUseCase,
} from '@/application/port/in/AmbientacaoUseCase';
import { buildOutroConteudoSlice } from '@/infrastructure/di/builders/outroConteudoBuilder';
import type {
  ListOutrosConteudosUseCase, CreateOutroConteudoUseCase, UpdateOutroConteudoUseCase, ArquivarOutroConteudoUseCase,
} from '@/application/port/in/OutroConteudoUseCase';
import { buildUnidadeItemSlice } from '@/infrastructure/di/builders/unidadeItemBuilder';
import type {
  ListUnidadeItensUseCase, CreateUnidadeItemUseCase, CreateUnidadeItemPdfUseCase,
  CreateUnidadeItemDaBibliotecaUseCase,
  ArquivarUnidadeItemUseCase, ReordenarUnidadeItensUseCase, BaixarUnidadeItemUseCase,
} from '@/application/port/in/UnidadeItemUseCase';
import type {
  ListAulasAoVivoUseCase, CreateAulaAoVivoUseCase, UpdateAulaAoVivoUseCase, ArquivarAulaAoVivoUseCase,
  ListMateriaisUseCase, CreateMaterialUseCase, UpdateMaterialUseCase, ArquivarMaterialUseCase,
  ListManuaisUseCase, CreateManualUseCase, UpdateManualUseCase, ArquivarManualUseCase,
  EnviarMensagemUseCase, ListMensagensByAlunoUseCase,
} from '@/application/port/in/ComunicacaoUseCase';
import { buildFundacaoSlice } from '@/infrastructure/di/builders/fundacaoBuilder';
import type {
  ListAgenciadoresUseCase, CreateAgenciadorUseCase, UpdateAgenciadorUseCase, RemoveAgenciadorUseCase,
  ListEmailTemplatesUseCase, CreateEmailTemplateUseCase, UpdateEmailTemplateUseCase,
  RemoveEmailTemplateUseCase, PreviewEmailTemplateUseCase,
  ListIntegracoesUseCase, CreateIntegracaoUseCase, UpdateIntegracaoUseCase, RemoveIntegracaoUseCase,
  GetTenantFinancialConfigUseCase, SaveTenantFinancialConfigUseCase,
} from '@/application/port/in/FundacaoUseCase';
import { buildComercialGapsSlice } from '@/infrastructure/di/builders/comercialGapsBuilder';
import type {
  ListGruposVendaUseCase, CreateGrupoVendaUseCase, UpdateGrupoVendaUseCase, RemoveGrupoVendaUseCase,
  ListLinksCaptacaoUseCase, CreateLinkCaptacaoUseCase, UpdateLinkCaptacaoUseCase, RemoveLinkCaptacaoUseCase,
  ListProcessosSeletivosUseCase, CreateProcessoSeletivoUseCase, UpdateProcessoSeletivoUseCase,
  AbrirProcessoSeletivoUseCase, EncerrarProcessoSeletivoUseCase, RemoveProcessoSeletivoUseCase,
  ListInscricoesUseCase, CriarInscricaoUseCase, AprovarInscricaoUseCase, ReprovarInscricaoUseCase,
} from '@/application/port/in/ComercialGapsUseCase';
import { buildAcadGapsSlice } from '@/infrastructure/di/builders/acadGapsBuilder';
import type {
  ListNoticiasUseCase, CreateNoticiaUseCase, UpdateNoticiaUseCase, RemoveNoticiaUseCase,
  GetNotasMultidisciplinarUseCase, GetCpaRelatorioAnaliticoUseCase,
} from '@/application/port/in/AcadGapsUseCase';
import { buildRelatoriosSlice } from '@/infrastructure/di/builders/relatoriosBuilder';
import type {
  GetRelatorioInadimplentesUseCase, GetRelatorioAdimplentesUseCase,
  GetRelatorioPagamentosPorPoloUseCase, GetRelatorioAcessoAvaUseCase,
} from '@/application/port/in/RelatorioUseCase';
import { buildLivroRegistroSlice } from '@/infrastructure/di/builders/livroRegistroBuilder';
import type {
  ListLivrosRegistroUseCase, AbrirLivroRegistroUseCase, FecharLivroRegistroUseCase,
  ListLivroRegistroItensUseCase, AddLivroRegistroItemUseCase,
} from '@/application/port/in/LivroRegistroUseCase';
import type {
  ListOfertasUseCase, CreateOfertaUseCase, UpdateOfertaUseCase, MudarStatusOfertaUseCase,
  ListAulasUseCase, RegistrarAulaUseCase, AtualizarAulaUseCase, ExcluirAulaUseCase,
} from '@/application/port/in/OfertaUseCase';
import type {
  GetProtocoloUseCase, ListProtocoloThreadUseCase, ListProtocoloTransferenciasUseCase,
  AbrirProtocoloUseCase, ResponderProtocoloUseCase, EncerrarProtocoloUseCase,
  ReabrirProtocoloUseCase, TransferirProtocoloUseCase,
  ListProtocoloAnexosUseCase, UploadProtocoloAnexoUseCase,
} from '@/application/port/in/ProtocoloWorkflowUseCase';
import type {
  SolicitarBolsaUseCase, AprovarBolsaUseCase, RevogarBolsaUseCase,
} from '@/application/port/in/BolsaUseCase';
import type {
  EmitirNfseUseCase, ConsultarNfseUseCase, EmitirCobrancaGatewayUseCase,
} from '@/application/port/in/FaturamentoUseCase';
import type {
  IssueParcelaPixUseCase, IssueParcelaBoletoUseCase,
} from '@/application/port/in/ParcelaArtifactUseCase';
import type {
  ListFormulariosUseCase, GetFormularioUseCase, CreateFormularioUseCase,
  UpdateFormularioUseCase, PublicarFormularioUseCase, ClonarFormularioUseCase,
  InativarFormularioUseCase, ListFormularioPerguntasUseCase,
  AddFormularioPerguntaUseCase, RemoverFormularioPerguntaUseCase,
} from '@/application/port/in/FormularioUseCase';
import type {
  ListCpaCiclosUseCase, GetCpaCicloUseCase, CriarCpaCicloUseCase,
  AgendarCpaCicloUseCase, ProrrogarCpaCicloUseCase, FecharCpaCicloUseCase,
  ArquivarCpaCicloUseCase, RelatorioSinteticoCpaUseCase, ListCpaDimensoesUseCase,
} from '@/application/port/in/CpaUseCase';
import type {
  ListRequerimentoTiposUseCase, CreateRequerimentoTipoUseCase, UpdateRequerimentoTipoUseCase,
  DeactivateRequerimentoTipoUseCase, ListRequerimentosUseCase, GetRequerimentoUseCase,
  ListRequerimentoHistoricoUseCase, FilaRequerimentoUseCase, ClaimRequerimentoEtapaUseCase,
  DecidirRequerimentoEtapaUseCase, PedirComplementacaoRequerimentoUseCase,
  ReenviarRequerimentoUseCase, ReverterRequerimentoUseCase,
  GetRequerimentoPagamentoUseCase, ConfirmarRequerimentoPagamentoUseCase,
} from '@/application/port/in/RequerimentoAdminUseCase';
import type {
  ListContratoTiposUseCase, CreateContratoTipoUseCase, UpdateContratoTipoUseCase, DeactivateContratoTipoUseCase,
  ListContratoTemplatesUseCase, GetContratoTemplateUseCase, CreateContratoTemplateUseCase,
  UpdateContratoTemplateUseCase, DeactivateContratoTemplateUseCase,
  ListCertificadoTemplatesUseCase, GetCertificadoTemplateUseCase, CreateCertificadoTemplateUseCase,
  UpdateCertificadoTemplateUseCase, DeactivateCertificadoTemplateUseCase,
} from '@/application/port/in/TemplateUseCase';
import type {
  ListParticipantesUseCase, AdicionarParticipanteUseCase, ConfirmarParticipanteUseCase,
  CancelarParticipanteUseCase, RegistrarPresencaUseCase,
} from '@/application/port/in/AgendamentoParticipanteUseCase';
import type { ListDocentesByDisciplineUseCase } from '@/application/port/in/DisciplineDocentesUseCase';
import type {
  ListDocumentoTiposAdminUseCase, GetDocumentoTipoUseCase,
  CreateDocumentoTipoUseCase, UpdateDocumentoTipoUseCase, DeactivateDocumentoTipoUseCase,
} from '@/application/port/in/DocumentoTipoUseCase';
import type {
  ListHorasConfigsUseCase, CreateHorasConfigUseCase,
  UpdateHorasConfigUseCase, DeactivateHorasConfigUseCase,
  ListHorasCategoriasUseCase, CreateHorasCategoriaUseCase,
  UpdateHorasCategoriaUseCase, DeactivateHorasCategoriaUseCase,
  ListHorasFilaUseCase, GetHorasLancamentoUseCase,
  AprovarHorasLancamentoUseCase, RejeitarHorasLancamentoUseCase,
  DownloadHorasComprovanteUseCase,
} from '@/application/port/in/HorasComplementaresUseCase';
import type {
  ListFilaValidacaoUseCase, AprovarDocumentoUseCase,
  NegarDocumentoUseCase, PedirComplementacaoDocumentoUseCase,
} from '@/application/port/in/DocumentoAprovacaoUseCase';
import type {
  ListDocumentoModelosUseCase, GetDocumentoModeloUseCase,
  CreateDocumentoModeloUseCase, UpdateDocumentoModeloUseCase, DeactivateDocumentoModeloUseCase,
} from '@/application/port/in/DocumentoModeloUseCase';
import type {
  ListDocumentosEmitidosByAlunoUseCase, GetDocumentoEmitidoUseCase, EmitirDocumentoUseCase,
} from '@/application/port/in/DocumentoEmissaoUseCase';
import type {
  ListProtocoloSetoresUseCase, GetProtocoloSetorUseCase, CreateProtocoloSetorUseCase,
  UpdateProtocoloSetorUseCase, DeactivateProtocoloSetorUseCase,
  ListProtocoloAssuntosUseCase, GetProtocoloAssuntoUseCase, CreateProtocoloAssuntoUseCase,
  UpdateProtocoloAssuntoUseCase, DeactivateProtocoloAssuntoUseCase,
} from '@/application/port/in/ProtocoloCadastrosUseCase';
import type {
  ListFilaPorSetorUseCase, ClaimProtocoloUseCase,
} from '@/application/port/in/ProtocoloAdminUseCase';
import type { GetHistoricoEscolarUseCase } from '@/application/port/in/HistoricoEscolarUseCase';
import type {
  ListCertificadosUseCase, ListCertificadosByAlunoUseCase, GetCertificadoUseCase,
  MarcarCertificadoBaixadoUseCase, RevogarCertificadoUseCase,
} from '@/application/port/in/CertificadoUseCase';
import type {
  ListAgendamentoRecursosUseCase, GetAgendamentoRecursoUseCase,
  CreateAgendamentoRecursoUseCase, UpdateAgendamentoRecursoUseCase,
  DeactivateAgendamentoRecursoUseCase,
} from '@/application/port/in/AgendamentoRecursoUseCase';
import type {
  ListCalendarioUseCase, GetAgendamentoUseCase, CriarAgendamentoUseCase,
  ConfirmarAgendamentoUseCase, MarcarAgendamentoRealizadoUseCase,
  MarcarAgendamentoNoShowUseCase, CancelarAgendamentoUseCase,
} from '@/application/port/in/AgendamentoUseCase';
import type {
  ListSolicitacoesConclusaoUseCase, GetSolicitacaoConclusaoUseCase,
  ListEtapasSolicitacaoConclusaoUseCase, ListDocumentosSolicitacaoConclusaoUseCase,
  ListHistoricoSolicitacaoConclusaoUseCase,
  FilaSolicitacaoConclusaoUseCase, ClaimEtapaSolicitacaoConclusaoUseCase,
  DecidirEtapaSolicitacaoConclusaoUseCase, PedirComplementacaoSolicitacaoConclusaoUseCase,
  RegistrarAcademicoSolicitacaoConclusaoUseCase, ReenviarSolicitacaoConclusaoUseCase,
  ReverterSolicitacaoConclusaoUseCase,
} from '@/application/port/in/SolicitacaoConclusaoUseCase';
import type {
  ListLgpdSolicitacoesAdminUseCase, ProcessarLgpdSolicitacaoUseCase,
} from '@/application/port/in/LgpdAdminUseCase';
import type { GetAcademicoDashboardUseCase } from '@/application/port/in/GetAcademicoDashboardUseCase';
import type { AuthGateway } from '@/application/port/out/AuthGateway';
import type { AlunoGateway } from '@/application/port/out/AlunoGateway';
import type { RacaGateway } from '@/application/port/out/RacaGateway';
import type { CepGateway } from '@/application/port/out/CepGateway';
import type { DocumentoGateway } from '@/application/port/out/DocumentoGateway';
import type { RequerimentoGateway } from '@/application/port/out/RequerimentoGateway';
import type { ProtocoloGateway } from '@/application/port/out/ProtocoloGateway';
import type { ObservacaoAlunoGateway } from '@/application/port/out/ObservacaoAlunoGateway';
import type { TenantFeatureGateway } from '@/application/port/out/TenantFeatureGateway';
import type { AlunoStatusGateway } from '@/application/port/out/AlunoStatusGateway';
import type { AlunoBloqueioAcessoGateway } from '@/application/port/out/AlunoBloqueioAcessoGateway';
import type {
  ListTenantFeaturesUseCase, ToggleFeatureUseCase, ApplyProfileUseCase,
} from '@/application/port/in/TenantFeatureUseCase';
import type {
  AlterarStatusAlunoUseCase, ListAlunoStatusHistoricoUseCase,
} from '@/application/port/in/AlunoStatusUseCase';
import type {
  BloquearAcessoAlunoUseCase, DesbloquearAcessoAlunoUseCase,
  ListBloqueiosAcessoUseCase, FindBloqueioAtivoUseCase,
} from '@/application/port/in/AlunoBloqueioAcessoUseCase';
import type { GetAlunoVisao360UseCase } from '@/application/port/in/GetAlunoVisao360UseCase';
import type { AlunoVisao360Gateway } from '@/application/port/out/AlunoVisao360Gateway';
import type { UploadAlunoFotoUseCase } from '@/application/port/in/UploadAlunoFotoUseCase';
import type { DownloadAlunoFotoUseCase } from '@/application/port/in/DownloadAlunoFotoUseCase';
import type {
  ListNotasByMatriculaUseCase, LancarNotaUseCase,
} from '@/application/port/in/NotaUseCase';
import type { NotaGateway } from '@/application/port/out/NotaGateway';
import type { GetBoletimUseCase } from '@/application/port/in/GetBoletimUseCase';
import type { BoletimGateway } from '@/application/port/out/BoletimGateway';
import type {
  ListFrequenciasByMatriculaUseCase, LancarFrequenciaUseCase,
} from '@/application/port/in/FrequenciaUseCase';
import type { FrequenciaGateway } from '@/application/port/out/FrequenciaGateway';
import type { CohortGateway } from '@/application/port/out/CohortGateway';
import type { CatalogReadGateway } from '@/application/port/out/CatalogReadGateway';
import type { CourseGateway } from '@/application/port/out/CourseGateway';
import type { IesGateway } from '@/application/port/out/IesGateway';
import type { DisciplineGateway } from '@/application/port/out/DisciplineGateway';
import type { CurriculumGateway } from '@/application/port/out/CurriculumGateway';
import type { MatriculaGateway } from '@/application/port/out/MatriculaGateway';
import type { ContratoGateway } from '@/application/port/out/ContratoGateway';
import type { ParcelaGateway } from '@/application/port/out/ParcelaGateway';
import type { SessionStorageGateway } from '@/application/port/out/SessionStorageGateway';
import type {
  ForgotPasswordUseCase,
  LoginUseCase,
  LogoutUseCase,
  ResetPasswordUseCase,
} from '@/application/port/in/LoginUseCase';
import type {
  CreateAlunoUseCase,
  GetAlunoUseCase,
  ListAlunosUseCase,
  UpdateAlunoUseCase,
} from '@/application/port/in/AlunoUseCase';
import type { ListRacasUseCase } from '@/application/port/in/RacaUseCase';
import type { LookupCepUseCase } from '@/application/port/in/CepUseCase';
import type {
  ListDocumentosAlunoUseCase,
  ListDocumentoTiposUseCase,
  ListRequerimentosAlunoUseCase,
  ListProtocolosAlunoUseCase,
  ListObservacoesAlunoUseCase,
  CreateObservacaoAlunoUseCase,
} from '@/application/port/in/PerfilAlunoTabsUseCase';
import type {
  CreateCohortUseCase,
  GetCohortUseCase,
  ListCohortsUseCase,
  UpdateCohortUseCase,
} from '@/application/port/in/CohortUseCase';
import type {
  ListCoursesUseCase,
  ListCurriculumsByCourseUseCase,
} from '@/application/port/in/CatalogQueryUseCase';
import type {
  ArchiveCourseUseCase,
  CreateCourseUseCase,
  GetCourseUseCase,
  ListCoursesPaginatedUseCase,
  PublishCourseUseCase,
  UpdateCourseUseCase,
  ListCourseDisciplinesUseCase,
  ChooseCourseCurriculumUseCase,
} from '@/application/port/in/CourseUseCase';
import type { GetCurrentIesUseCase } from '@/application/port/in/IesUseCase';
import type {
  ListPolosUseCase, GetPoloUseCase, CreatePoloUseCase, UpdatePoloUseCase, DeactivatePoloUseCase,
} from '@/application/port/in/PoloUseCase';
import type {
  ListSectorsUseCase, GetSectorUseCase, CreateSectorUseCase, UpdateSectorUseCase, DeactivateSectorUseCase,
} from '@/application/port/in/SectorUseCase';
import type {
  ListMantenedorasUseCase, GetMantenedoraUseCase, CreateMantenedoraUseCase,
  UpdateMantenedoraUseCase, DeactivateMantenedoraUseCase,
} from '@/application/port/in/MantenedoraUseCase';
import type { OnboardTenantUseCase } from '@/application/port/in/TenantOnboardingUseCase';
import type {
  ListUsersUseCase, CreateUserUseCase, UpdateUserUseCase,
  DeactivateUserUseCase, BlockUserUseCase, UnblockUserUseCase,
} from '@/application/port/in/UserUseCase';
import type {
  ListRolesUseCase, ListPermissionsUseCase, CreateRoleUseCase,
  UpdateRoleUseCase, SetRolePermissionsUseCase, DeactivateRoleUseCase,
} from '@/application/port/in/RoleUseCase';
import type {
  ListInvitationsUseCase, CreateInvitationUseCase,
  ResendInvitationUseCase, RevokeInvitationUseCase, AcceptInvitationUseCase,
} from '@/application/port/in/InvitationUseCase';
import type {
  GetMeUseCase, UpdateMeUseCase, ChangePasswordUseCase,
  SetupTotpUseCase, EnableTotpUseCase, DisableTotpUseCase,
} from '@/application/port/in/MeUseCase';
import type {
  ExportarDadosLgpdUseCase, SolicitarExclusaoLgpdUseCase,
  ListConsentimentosUseCase, RegistrarConsentimentoUseCase,
} from '@/application/port/in/LgpdUseCase';
import type {
  ListLossReasonsUseCase, CreateLossReasonUseCase,
  UpdateLossReasonUseCase, DeleteLossReasonUseCase,
} from '@/application/port/in/LossReasonUseCase';
import type {
  ListCadenciasUseCase, CreateCadenciaUseCase, UpdateCadenciaUseCase, DeleteCadenciaUseCase,
} from '@/application/port/in/CadenciaUseCase';
import type {
  ListGatewayCredenciaisUseCase, CreateGatewayCredencialUseCase,
  UpdateGatewayCredencialUseCase, DeleteGatewayCredencialUseCase,
} from '@/application/port/in/GatewayCredencialUseCase';
import type { ListActivitiesUseCase } from '@/application/port/in/ActivityUseCase';
import type {
  ListSolicitacoesDescontoExtraUseCase, DecidirDescontoExtraUseCase,
} from '@/application/port/in/SolicitacaoDescontoExtraUseCase';
import type {
  ActivateDisciplineUseCase,
  ArchiveDisciplineUseCase,
  CreateDisciplineUseCase,
  GetDisciplineUseCase,
  ListDisciplinesUseCase,
  UpdateDisciplineUseCase,
} from '@/application/port/in/DisciplineUseCase';
import type {
  ArchiveCurriculumUseCase,
  CreateCurriculumUseCase,
  GetCurriculumUseCase,
  ListCurriculumsUseCase, ListCurriculumsByCourseFullUseCase,
  PublishCurriculumUseCase,
  UpdateCurriculumUseCase,
} from '@/application/port/in/CurriculumUseCase';
import type {
  CreateMatriculaUseCase,
  GetMatriculaUseCase,
  ListMatriculasByAlunoUseCase,
  ListMatriculasUseCase,
  TransitionMatriculaStatusUseCase,
} from '@/application/port/in/MatriculaUseCase';
import type {
  CancelarContratoUseCase,
  GerarContratoUseCase,
  GetContratoUseCase,
  ListContratosByMatriculaUseCase,
  RenderizarContratoUseCase,
  RegistrarAssinaturaContratoUseCase,
} from '@/application/port/in/ContratoUseCase';
import type {
  GetContratoAssinaturaUseCase, GerarContratoAssinaturaUseCase,
  RecusarContratoUseCase, RevogarContratoUseCase,
} from '@/application/port/in/ContratoAssinaturaUseCase';
import type {
  CancelarParcelaUseCase,
  CreateParcelaUseCase,
  ListParcelasUseCase,
  PagarParcelaUseCase,
} from '@/application/port/in/ParcelaUseCase';
import type { AuthSession } from '@/domain/model/User';

export interface Container {
  readonly authGateway: AuthGateway;
  readonly alunoGateway: AlunoGateway;
  readonly racaGateway: RacaGateway;
  readonly cepGateway: CepGateway;
  readonly documentoGateway: DocumentoGateway;
  readonly requerimentoGateway: RequerimentoGateway;
  readonly protocoloGateway: ProtocoloGateway;
  readonly observacaoAlunoGateway: ObservacaoAlunoGateway;
  readonly tenantFeatureGateway: TenantFeatureGateway;
  readonly alunoStatusGateway: AlunoStatusGateway;
  readonly alunoBloqueioAcessoGateway: AlunoBloqueioAcessoGateway;
  readonly alunoVisao360Gateway: AlunoVisao360Gateway;
  readonly notaGateway: NotaGateway;
  readonly boletimGateway: BoletimGateway;
  readonly frequenciaGateway: FrequenciaGateway;
  readonly cohortGateway: CohortGateway;
  readonly catalogReadGateway: CatalogReadGateway;
  readonly courseGateway: CourseGateway;
  readonly iesGateway: IesGateway;
  readonly disciplineGateway: DisciplineGateway;
  readonly curriculumGateway: CurriculumGateway;
  readonly matriculaGateway: MatriculaGateway;
  readonly contratoGateway: ContratoGateway;
  readonly parcelaGateway: ParcelaGateway;
  readonly sessionStorage: SessionStorageGateway;
  readonly useCases: {
    readonly login: LoginUseCase;
    readonly logout: LogoutUseCase;
    readonly forgot: ForgotPasswordUseCase;
    readonly reset: ResetPasswordUseCase;
    readonly listAlunos: ListAlunosUseCase;
    readonly getAluno: GetAlunoUseCase;
    readonly createAluno: CreateAlunoUseCase;
    readonly updateAluno: UpdateAlunoUseCase;
    readonly listRacas: ListRacasUseCase;
    readonly lookupCep: LookupCepUseCase;
    readonly listDocumentosAluno: ListDocumentosAlunoUseCase;
    readonly listDocumentoTipos: ListDocumentoTiposUseCase;
    readonly listRequerimentosAluno: ListRequerimentosAlunoUseCase;
    readonly listProtocolosAluno: ListProtocolosAlunoUseCase;
    readonly listObservacoesAluno: ListObservacoesAlunoUseCase;
    readonly createObservacaoAluno: CreateObservacaoAlunoUseCase;
    readonly listTenantFeatures: ListTenantFeaturesUseCase;
    readonly toggleFeature: ToggleFeatureUseCase;
    readonly applyProfile: ApplyProfileUseCase;
    readonly alterarStatusAluno: AlterarStatusAlunoUseCase;
    readonly listAlunoStatusHistorico: ListAlunoStatusHistoricoUseCase;
    readonly bloquearAcessoAluno: BloquearAcessoAlunoUseCase;
    readonly desbloquearAcessoAluno: DesbloquearAcessoAlunoUseCase;
    readonly listBloqueiosAcesso: ListBloqueiosAcessoUseCase;
    readonly findBloqueioAtivo: FindBloqueioAtivoUseCase;
    readonly getAlunoVisao360: GetAlunoVisao360UseCase;
    readonly uploadAlunoFoto: UploadAlunoFotoUseCase;
    readonly downloadAlunoFoto: DownloadAlunoFotoUseCase;
    // Conteúdo externo multi-provedor (Provedor + Unidade + Conteúdo externo)
    readonly listContentProviders: ListContentProvidersUseCase;
    readonly createContentProvider: CreateContentProviderUseCase;
    readonly updateContentProvider: UpdateContentProviderUseCase;
    readonly archiveContentProvider: ArchiveContentProviderUseCase;
    readonly browseProviderCatalog: BrowseProviderCatalogUseCase;
    readonly listUnidades: ListUnidadesUseCase;
    readonly createUnidade: CreateUnidadeUseCase;
    readonly updateUnidade: UpdateUnidadeUseCase;
    readonly archiveUnidade: ArchiveUnidadeUseCase;
    readonly listExternalContents: ListExternalContentsUseCase;
    readonly createExternalContent: CreateExternalContentUseCase;
    readonly updateExternalContent: UpdateExternalContentUseCase;
    readonly archiveExternalContent: ArchiveExternalContentUseCase;
    readonly listNotasByMatricula: ListNotasByMatriculaUseCase;
    readonly lancarNota: LancarNotaUseCase;
    readonly getBoletim: GetBoletimUseCase;
    readonly listFrequenciasByMatricula: ListFrequenciasByMatriculaUseCase;
    readonly lancarFrequencia: LancarFrequenciaUseCase;
    readonly getMatriculaDisciplines: GetMatriculaDisciplinesUseCase;
    readonly getEngajamento: GetEngajamentoUseCase;
    readonly verificarContatoAluno: VerificarContatoAlunoUseCase;
    readonly getFinanceiroDashboard: GetFinanceiroDashboardUseCase;
    readonly listPagamentosByParcela: ListPagamentosByParcelaUseCase;
    readonly registrarPagamentoManual: RegistrarPagamentoManualUseCase;
    readonly listCobrancaReguaPassos: ListCobrancaReguaPassosUseCase;
    readonly createCobrancaReguaPasso: CreateCobrancaReguaPassoUseCase;
    readonly updateCobrancaReguaPasso: UpdateCobrancaReguaPassoUseCase;
    readonly deleteCobrancaReguaPasso: DeleteCobrancaReguaPassoUseCase;
    readonly runReguaCobranca: RunReguaCobrancaUseCase;
    readonly listRenegociacoes: ListRenegociacoesUseCase;
    readonly solicitarRenegociacao: SolicitarRenegociacaoUseCase;
    readonly decidirRenegociacao: DecidirRenegociacaoUseCase;
    readonly listEstornos: ListEstornosUseCase;
    readonly solicitarEstorno: SolicitarEstornoUseCase;
    readonly decidirEstorno: DecidirEstornoUseCase;
    readonly processarEstornoGateway: ProcessarEstornoGatewayUseCase;
    readonly listVendas: ListVendasUseCase;
    readonly getVenda: GetVendaUseCase;
    readonly listVendaParcelas: ListVendaParcelasUseCase;
    readonly listVendaDescontos: ListVendaDescontosUseCase;
    readonly cancelarVenda: CancelarVendaUseCase;
    readonly listAdminParcelas: ListAdminParcelasUseCase;
    readonly listAdminPagamentos: ListAdminPagamentosUseCase;
    readonly listCobrancaEnvios: ListCobrancaEnviosUseCase;
    readonly listCobrancaEnviosByParcela: ListCobrancaEnviosByParcelaUseCase;
    readonly listCommissionRules: ListCommissionRulesUseCase;
    readonly getCommissionRule: GetCommissionRuleUseCase;
    readonly criarCommissionRule: CriarCommissionRuleUseCase;
    readonly atualizarCommissionRule: AtualizarCommissionRuleUseCase;
    readonly desativarCommissionRule: DesativarCommissionRuleUseCase;
    readonly listCommissionEntries: ListCommissionEntriesUseCase;
    readonly apropriarCommissionEntry: ApropriarCommissionEntryUseCase;
    readonly pagarCommissionEntry: PagarCommissionEntryUseCase;
    readonly listPrecos: ListPrecosUseCase;
    readonly getPreco: GetPrecoUseCase;
    readonly criarPreco: CriarPrecoUseCase;
    readonly atualizarPreco: AtualizarPrecoUseCase;
    readonly inativarPreco: InativarPrecoUseCase;
    readonly listPrecoMeios: ListPrecoMeiosUseCase;
    readonly addPrecoMeio: AddPrecoMeioUseCase;
    readonly removePrecoMeio: RemovePrecoMeioUseCase;
    readonly listDescontos: ListDescontosUseCase;
    readonly getDesconto: GetDescontoUseCase;
    readonly criarDesconto: CriarDescontoUseCase;
    readonly ativarDesconto: AtivarDescontoUseCase;
    readonly desativarDesconto: DesativarDescontoUseCase;
    readonly listWebhookSubscriptions: ListWebhookSubscriptionsUseCase;
    readonly getWebhookSubscription: GetWebhookSubscriptionUseCase;
    readonly criarWebhookSubscription: CriarWebhookSubscriptionUseCase;
    readonly atualizarWebhookSubscription: AtualizarWebhookSubscriptionUseCase;
    readonly desativarWebhookSubscription: DesativarWebhookSubscriptionUseCase;
    readonly listWebhookDeliveries: ListWebhookDeliveriesUseCase;
    readonly replayWebhookDelivery: ReplayWebhookDeliveryUseCase;
    readonly listMetasVendas: ListMetasVendasUseCase;
    readonly getMetaVendas: GetMetaVendasUseCase;
    readonly criarMetaVendas: CriarMetaVendasUseCase;
    readonly atualizarMetaVendas: AtualizarMetaVendasUseCase;
    readonly desativarMetaVendas: DesativarMetaVendasUseCase;
    readonly listAlertasVenda: ListAlertasVendaUseCase;
    readonly getAlertaVenda: GetAlertaVendaUseCase;
    readonly deferirAlertaVenda: DeferirAlertaVendaUseCase;
    readonly indeferirAlertaVenda: IndeferirAlertaVendaUseCase;
    readonly getTenantPagamentoConfig: GetTenantPagamentoConfigUseCase;
    readonly upsertTenantPagamentoConfig: UpsertTenantPagamentoConfigUseCase;
    readonly listLeads: ListLeadsUseCase;
    readonly getLead: GetLeadUseCase;
    readonly moverLeadStage: MoverLeadStageUseCase;
    readonly marcarLeadPerdido: MarcarLeadPerdidoUseCase;
    readonly listPipelines: ListPipelinesUseCase;
    readonly listPipelineStages: ListPipelineStagesUseCase;
    readonly createPipeline: CreatePipelineUseCase;
    readonly updatePipeline: UpdatePipelineUseCase;
    readonly deactivatePipeline: DeactivatePipelineUseCase;
    readonly listOpportunities: ListOpportunitiesUseCase;
    readonly getOpportunity: GetOpportunityUseCase;
    readonly criarOpportunity: CriarOpportunityUseCase;
    readonly transicionarOpportunity: TransicionarOpportunityUseCase;
    readonly converterOpportunity: ConverterOpportunityUseCase;
    readonly listMetodosAvaliativos: ListMetodosAvaliativosUseCase;
    readonly createMetodoAvaliativo: CreateMetodoAvaliativoUseCase;
    readonly updateMetodoAvaliativo: UpdateMetodoAvaliativoUseCase;
    readonly deleteMetodoAvaliativo: DeleteMetodoAvaliativoUseCase;
    readonly listPeriodosLetivos: ListPeriodosLetivosUseCase;
    readonly createPeriodoLetivo: CreatePeriodoLetivoUseCase;
    readonly updatePeriodoLetivo: UpdatePeriodoLetivoUseCase;
    readonly marcarVigentePeriodo: MarcarVigentePeriodoUseCase;
    readonly encerrarPeriodo: EncerrarPeriodoUseCase;
    readonly listCalendarioEventos: ListCalendarioEventosUseCase;
    readonly createCalendarioEvento: CreateCalendarioEventoUseCase;
    readonly updateCalendarioEvento: UpdateCalendarioEventoUseCase;
    readonly deleteCalendarioEvento: DeleteCalendarioEventoUseCase;
    readonly listAvaliacoes: ListAvaliacoesUseCase;
    readonly getAvaliacao: GetAvaliacaoUseCase;
    readonly createAvaliacao: CreateAvaliacaoUseCase;
    readonly updateAvaliacao: UpdateAvaliacaoUseCase;
    readonly publishAvaliacao: PublishAvaliacaoUseCase;
    readonly archiveAvaliacao: ArchiveAvaliacaoUseCase;
    readonly deleteAvaliacao: DeleteAvaliacaoUseCase;
    readonly listAvaliacaoQuestoes: ListAvaliacaoQuestoesUseCase;
    readonly addQuestaoToAvaliacao: AddQuestaoToAvaliacaoUseCase;
    readonly removeQuestaoFromAvaliacao: RemoveQuestaoFromAvaliacaoUseCase;
    readonly listQuestoes: ListQuestoesUseCase;
    readonly listTentativas: ListTentativasUseCase;
    readonly getTentativa: GetTentativaUseCase;
    readonly listDocentes: ListDocentesUseCase;
    readonly getDocente: GetDocenteUseCase;
    readonly createDocente: CreateDocenteUseCase;
    readonly updateDocente: UpdateDocenteUseCase;
    readonly archiveDocente: ArchiveDocenteUseCase;
    readonly listDocenteAssignments: ListDocenteAssignmentsUseCase;
    readonly assignDocente: AssignDocenteUseCase;
    readonly unassignDocente: UnassignDocenteUseCase;
    readonly getQuestao: GetQuestaoUseCase;
    readonly createQuestao: CreateQuestaoUseCase;
    readonly updateQuestao: UpdateQuestaoUseCase;
    readonly deleteQuestao: DeleteQuestaoUseCase;
    readonly listCohorts: ListCohortsUseCase;
    readonly getCohort: GetCohortUseCase;
    readonly createCohort: CreateCohortUseCase;
    readonly updateCohort: UpdateCohortUseCase;
    readonly listCourses: ListCoursesUseCase;
    readonly listCurriculumsByCourse: ListCurriculumsByCourseUseCase;
    readonly listCoursesPaginated: ListCoursesPaginatedUseCase;
    readonly getCourse: GetCourseUseCase;
    readonly createCourse: CreateCourseUseCase;
    readonly updateCourse: UpdateCourseUseCase;
    readonly publishCourse: PublishCourseUseCase;
    readonly archiveCourse: ArchiveCourseUseCase;
    readonly chooseCourseCurriculum: ChooseCourseCurriculumUseCase;
    readonly listCourseDisciplines: ListCourseDisciplinesUseCase;
    readonly getCurrentIes: GetCurrentIesUseCase;
    readonly listPolos: ListPolosUseCase;
    readonly getPolo: GetPoloUseCase;
    readonly createPolo: CreatePoloUseCase;
    readonly updatePolo: UpdatePoloUseCase;
    readonly deactivatePolo: DeactivatePoloUseCase;
    readonly listSectors: ListSectorsUseCase;
    readonly getSector: GetSectorUseCase;
    readonly createSector: CreateSectorUseCase;
    readonly updateSector: UpdateSectorUseCase;
    readonly deactivateSector: DeactivateSectorUseCase;
    readonly listMantenedoras: ListMantenedorasUseCase;
    readonly getMantenedora: GetMantenedoraUseCase;
    readonly createMantenedora: CreateMantenedoraUseCase;
    readonly updateMantenedora: UpdateMantenedoraUseCase;
    readonly deactivateMantenedora: DeactivateMantenedoraUseCase;
    readonly onboardTenant: OnboardTenantUseCase;
    readonly listUsers: ListUsersUseCase;
    readonly createUser: CreateUserUseCase;
    readonly updateUser: UpdateUserUseCase;
    readonly deactivateUser: DeactivateUserUseCase;
    readonly blockUser: BlockUserUseCase;
    readonly unblockUser: UnblockUserUseCase;
    readonly listRoles: ListRolesUseCase;
    readonly listPermissions: ListPermissionsUseCase;
    readonly createRole: CreateRoleUseCase;
    readonly updateRole: UpdateRoleUseCase;
    readonly setRolePermissions: SetRolePermissionsUseCase;
    readonly deactivateRole: DeactivateRoleUseCase;
    readonly acceptInvitation: AcceptInvitationUseCase;
    readonly listInvitations: ListInvitationsUseCase;
    readonly createInvitation: CreateInvitationUseCase;
    readonly resendInvitation: ResendInvitationUseCase;
    readonly revokeInvitation: RevokeInvitationUseCase;
    readonly getMe: GetMeUseCase;
    readonly updateMe: UpdateMeUseCase;
    readonly changePassword: ChangePasswordUseCase;
    readonly setupTotp: SetupTotpUseCase;
    readonly enableTotp: EnableTotpUseCase;
    readonly disableTotp: DisableTotpUseCase;
    readonly exportarDadosLgpd: ExportarDadosLgpdUseCase;
    readonly solicitarExclusaoLgpd: SolicitarExclusaoLgpdUseCase;
    readonly listConsentimentos: ListConsentimentosUseCase;
    readonly registrarConsentimento: RegistrarConsentimentoUseCase;
    readonly listLossReasons: ListLossReasonsUseCase;
    readonly createLossReason: CreateLossReasonUseCase;
    readonly updateLossReason: UpdateLossReasonUseCase;
    readonly deleteLossReason: DeleteLossReasonUseCase;
    readonly listCadencias: ListCadenciasUseCase;
    readonly createCadencia: CreateCadenciaUseCase;
    readonly updateCadencia: UpdateCadenciaUseCase;
    readonly deleteCadencia: DeleteCadenciaUseCase;
    readonly listGatewayCredenciais: ListGatewayCredenciaisUseCase;
    readonly createGatewayCredencial: CreateGatewayCredencialUseCase;
    readonly updateGatewayCredencial: UpdateGatewayCredencialUseCase;
    readonly deleteGatewayCredencial: DeleteGatewayCredencialUseCase;
    readonly listActivities: ListActivitiesUseCase;
    readonly listSolicitacoesDescontoExtra: ListSolicitacoesDescontoExtraUseCase;
    readonly decidirDescontoExtra: DecidirDescontoExtraUseCase;
    readonly listDisciplines: ListDisciplinesUseCase;
    readonly getDiscipline: GetDisciplineUseCase;
    readonly createDiscipline: CreateDisciplineUseCase;
    readonly updateDiscipline: UpdateDisciplineUseCase;
    readonly activateDiscipline: ActivateDisciplineUseCase;
    readonly archiveDiscipline: ArchiveDisciplineUseCase;
    readonly listCurriculums: ListCurriculumsUseCase;
    readonly listCurriculumsFull: ListCurriculumsByCourseFullUseCase;
    readonly getCurriculum: GetCurriculumUseCase;
    readonly createCurriculum: CreateCurriculumUseCase;
    readonly updateCurriculum: UpdateCurriculumUseCase;
    readonly publishCurriculum: PublishCurriculumUseCase;
    readonly archiveCurriculum: ArchiveCurriculumUseCase;
    readonly listMatriculas: ListMatriculasUseCase;
    readonly getMatricula: GetMatriculaUseCase;
    readonly listMatriculasByAluno: ListMatriculasByAlunoUseCase;
    readonly createMatricula: CreateMatriculaUseCase;
    readonly transitionMatriculaStatus: TransitionMatriculaStatusUseCase;
    readonly listContratos: ListContratosByMatriculaUseCase;
    readonly getContrato: GetContratoUseCase;
    readonly gerarContrato: GerarContratoUseCase;
    readonly renderizarContrato: RenderizarContratoUseCase;
    readonly cancelarContrato: CancelarContratoUseCase;
    readonly registrarAssinaturaContrato: RegistrarAssinaturaContratoUseCase;
    readonly getContratoAssinatura: GetContratoAssinaturaUseCase;
    readonly gerarContratoAssinatura: GerarContratoAssinaturaUseCase;
    readonly recusarContrato: RecusarContratoUseCase;
    readonly revogarContrato: RevogarContratoUseCase;
    readonly listParcelas: ListParcelasUseCase;
    readonly createParcela: CreateParcelaUseCase;
    readonly pagarParcela: PagarParcelaUseCase;
    readonly cancelarParcela: CancelarParcelaUseCase;
    readonly listHorasConfigs: ListHorasConfigsUseCase;
    readonly createHorasConfig: CreateHorasConfigUseCase;
    readonly updateHorasConfig: UpdateHorasConfigUseCase;
    readonly deactivateHorasConfig: DeactivateHorasConfigUseCase;
    readonly listHorasCategorias: ListHorasCategoriasUseCase;
    readonly createHorasCategoria: CreateHorasCategoriaUseCase;
    readonly updateHorasCategoria: UpdateHorasCategoriaUseCase;
    readonly deactivateHorasCategoria: DeactivateHorasCategoriaUseCase;
    readonly listHorasFila: ListHorasFilaUseCase;
    readonly getHorasLancamento: GetHorasLancamentoUseCase;
    readonly aprovarHorasLancamento: AprovarHorasLancamentoUseCase;
    readonly rejeitarHorasLancamento: RejeitarHorasLancamentoUseCase;
    readonly downloadHorasComprovante: DownloadHorasComprovanteUseCase;
    readonly listDocumentoTiposAdmin: ListDocumentoTiposAdminUseCase;
    readonly getDocumentoTipo: GetDocumentoTipoUseCase;
    readonly createDocumentoTipo: CreateDocumentoTipoUseCase;
    readonly updateDocumentoTipo: UpdateDocumentoTipoUseCase;
    readonly deactivateDocumentoTipo: DeactivateDocumentoTipoUseCase;
    readonly listFilaValidacao: ListFilaValidacaoUseCase;
    readonly aprovarDocumento: AprovarDocumentoUseCase;
    readonly negarDocumento: NegarDocumentoUseCase;
    readonly pedirComplementacaoDocumento: PedirComplementacaoDocumentoUseCase;
    readonly listDocumentoModelos: ListDocumentoModelosUseCase;
    readonly getDocumentoModelo: GetDocumentoModeloUseCase;
    readonly createDocumentoModelo: CreateDocumentoModeloUseCase;
    readonly updateDocumentoModelo: UpdateDocumentoModeloUseCase;
    readonly deactivateDocumentoModelo: DeactivateDocumentoModeloUseCase;
    readonly listDocumentosEmitidosByAluno: ListDocumentosEmitidosByAlunoUseCase;
    readonly getDocumentoEmitido: GetDocumentoEmitidoUseCase;
    readonly emitirDocumento: EmitirDocumentoUseCase;
    readonly listProtocoloSetores: ListProtocoloSetoresUseCase;
    readonly getProtocoloSetor: GetProtocoloSetorUseCase;
    readonly createProtocoloSetor: CreateProtocoloSetorUseCase;
    readonly updateProtocoloSetor: UpdateProtocoloSetorUseCase;
    readonly deactivateProtocoloSetor: DeactivateProtocoloSetorUseCase;
    readonly listProtocoloAssuntos: ListProtocoloAssuntosUseCase;
    readonly getProtocoloAssunto: GetProtocoloAssuntoUseCase;
    readonly createProtocoloAssunto: CreateProtocoloAssuntoUseCase;
    readonly updateProtocoloAssunto: UpdateProtocoloAssuntoUseCase;
    readonly deactivateProtocoloAssunto: DeactivateProtocoloAssuntoUseCase;
    readonly listFilaPorSetor: ListFilaPorSetorUseCase;
    readonly claimProtocolo: ClaimProtocoloUseCase;
    readonly getHistoricoEscolar: GetHistoricoEscolarUseCase;
    readonly listCertificados: ListCertificadosUseCase;
    readonly listCertificadosByAluno: ListCertificadosByAlunoUseCase;
    readonly getCertificado: GetCertificadoUseCase;
    readonly marcarCertificadoBaixado: MarcarCertificadoBaixadoUseCase;
    readonly revogarCertificado: RevogarCertificadoUseCase;
    readonly listAgendamentoRecursos: ListAgendamentoRecursosUseCase;
    readonly getAgendamentoRecurso: GetAgendamentoRecursoUseCase;
    readonly createAgendamentoRecurso: CreateAgendamentoRecursoUseCase;
    readonly updateAgendamentoRecurso: UpdateAgendamentoRecursoUseCase;
    readonly deactivateAgendamentoRecurso: DeactivateAgendamentoRecursoUseCase;
    readonly listCalendario: ListCalendarioUseCase;
    readonly getAgendamento: GetAgendamentoUseCase;
    readonly criarAgendamento: CriarAgendamentoUseCase;
    readonly confirmarAgendamento: ConfirmarAgendamentoUseCase;
    readonly marcarAgendamentoRealizado: MarcarAgendamentoRealizadoUseCase;
    readonly marcarAgendamentoNoShow: MarcarAgendamentoNoShowUseCase;
    readonly cancelarAgendamento: CancelarAgendamentoUseCase;
    readonly listSolicitacoesConclusao: ListSolicitacoesConclusaoUseCase;
    readonly getSolicitacaoConclusao: GetSolicitacaoConclusaoUseCase;
    readonly listEtapasSolicitacaoConclusao: ListEtapasSolicitacaoConclusaoUseCase;
    readonly listDocumentosSolicitacaoConclusao: ListDocumentosSolicitacaoConclusaoUseCase;
    readonly listHistoricoSolicitacaoConclusao: ListHistoricoSolicitacaoConclusaoUseCase;
    readonly filaSolicitacaoConclusao: FilaSolicitacaoConclusaoUseCase;
    readonly claimEtapaSolicitacaoConclusao: ClaimEtapaSolicitacaoConclusaoUseCase;
    readonly decidirEtapaSolicitacaoConclusao: DecidirEtapaSolicitacaoConclusaoUseCase;
    readonly pedirComplementacaoSolicitacaoConclusao: PedirComplementacaoSolicitacaoConclusaoUseCase;
    readonly registrarAcademicoSolicitacaoConclusao: RegistrarAcademicoSolicitacaoConclusaoUseCase;
    readonly reenviarSolicitacaoConclusao: ReenviarSolicitacaoConclusaoUseCase;
    readonly reverterSolicitacaoConclusao: ReverterSolicitacaoConclusaoUseCase;
    readonly listLgpdSolicitacoesAdmin: ListLgpdSolicitacoesAdminUseCase;
    readonly processarLgpdSolicitacao: ProcessarLgpdSolicitacaoUseCase;
    readonly getAcademicoDashboard: GetAcademicoDashboardUseCase;
    // acadadmin-gaps (G.5.14 Formulário)
    readonly listFormularios: ListFormulariosUseCase;
    readonly getFormulario: GetFormularioUseCase;
    readonly createFormulario: CreateFormularioUseCase;
    readonly updateFormulario: UpdateFormularioUseCase;
    readonly publicarFormulario: PublicarFormularioUseCase;
    readonly clonarFormulario: ClonarFormularioUseCase;
    readonly inativarFormulario: InativarFormularioUseCase;
    readonly listFormularioPerguntas: ListFormularioPerguntasUseCase;
    readonly addFormularioPergunta: AddFormularioPerguntaUseCase;
    readonly removerFormularioPergunta: RemoverFormularioPerguntaUseCase;
    // acadadmin-gaps (G.5.13 CPA)
    readonly listCpaCiclos: ListCpaCiclosUseCase;
    readonly getCpaCiclo: GetCpaCicloUseCase;
    readonly criarCpaCiclo: CriarCpaCicloUseCase;
    readonly agendarCpaCiclo: AgendarCpaCicloUseCase;
    readonly prorrogarCpaCiclo: ProrrogarCpaCicloUseCase;
    readonly fecharCpaCiclo: FecharCpaCicloUseCase;
    readonly arquivarCpaCiclo: ArquivarCpaCicloUseCase;
    readonly relatorioSinteticoCpa: RelatorioSinteticoCpaUseCase;
    readonly listCpaDimensoes: ListCpaDimensoesUseCase;
    // acadadmin-gaps (G.5.15 Requerimento)
    readonly listRequerimentoTipos: ListRequerimentoTiposUseCase;
    readonly createRequerimentoTipo: CreateRequerimentoTipoUseCase;
    readonly updateRequerimentoTipo: UpdateRequerimentoTipoUseCase;
    readonly deactivateRequerimentoTipo: DeactivateRequerimentoTipoUseCase;
    readonly listRequerimentos: ListRequerimentosUseCase;
    readonly getRequerimento: GetRequerimentoUseCase;
    readonly listRequerimentoHistorico: ListRequerimentoHistoricoUseCase;
    readonly filaRequerimento: FilaRequerimentoUseCase;
    readonly claimRequerimentoEtapa: ClaimRequerimentoEtapaUseCase;
    readonly decidirRequerimentoEtapa: DecidirRequerimentoEtapaUseCase;
    readonly pedirComplementacaoRequerimento: PedirComplementacaoRequerimentoUseCase;
    readonly reenviarRequerimento: ReenviarRequerimentoUseCase;
    readonly reverterRequerimento: ReverterRequerimentoUseCase;
    readonly getRequerimentoPagamento: GetRequerimentoPagamentoUseCase;
    readonly confirmarRequerimentoPagamento: ConfirmarRequerimentoPagamentoUseCase;
    // acadadmin-gaps (G.5.18 Templates contrato/certificado)
    readonly listContratoTipos: ListContratoTiposUseCase;
    readonly createContratoTipo: CreateContratoTipoUseCase;
    readonly updateContratoTipo: UpdateContratoTipoUseCase;
    readonly deactivateContratoTipo: DeactivateContratoTipoUseCase;
    readonly listContratoTemplates: ListContratoTemplatesUseCase;
    readonly getContratoTemplate: GetContratoTemplateUseCase;
    readonly createContratoTemplate: CreateContratoTemplateUseCase;
    readonly updateContratoTemplate: UpdateContratoTemplateUseCase;
    readonly deactivateContratoTemplate: DeactivateContratoTemplateUseCase;
    readonly listCertificadoTemplates: ListCertificadoTemplatesUseCase;
    readonly getCertificadoTemplate: GetCertificadoTemplateUseCase;
    readonly createCertificadoTemplate: CreateCertificadoTemplateUseCase;
    readonly updateCertificadoTemplate: UpdateCertificadoTemplateUseCase;
    readonly deactivateCertificadoTemplate: DeactivateCertificadoTemplateUseCase;
    // acadadmin-gaps (G.5.17 AgendamentoParticipante + DisciplineDocentes)
    readonly listParticipantes: ListParticipantesUseCase;
    readonly adicionarParticipante: AdicionarParticipanteUseCase;
    readonly confirmarParticipante: ConfirmarParticipanteUseCase;
    readonly cancelarParticipante: CancelarParticipanteUseCase;
    readonly registrarPresenca: RegistrarPresencaUseCase;
    readonly listDocentesByDiscipline: ListDocentesByDisciplineUseCase;
    // admin-gaps2 (G.5.19 Oferta + Diário)
    readonly listOfertas: ListOfertasUseCase;
    readonly createOferta: CreateOfertaUseCase;
    readonly updateOferta: UpdateOfertaUseCase;
    readonly mudarStatusOferta: MudarStatusOfertaUseCase;
    readonly listAulas: ListAulasUseCase;
    readonly registrarAula: RegistrarAulaUseCase;
    readonly atualizarAula: AtualizarAulaUseCase;
    readonly excluirAula: ExcluirAulaUseCase;
    // admin-gaps2 (G.5.16 Protocolo workflow)
    readonly getProtocolo: GetProtocoloUseCase;
    readonly listProtocoloThread: ListProtocoloThreadUseCase;
    readonly listProtocoloTransferencias: ListProtocoloTransferenciasUseCase;
    readonly abrirProtocolo: AbrirProtocoloUseCase;
    readonly responderProtocolo: ResponderProtocoloUseCase;
    readonly encerrarProtocolo: EncerrarProtocoloUseCase;
    readonly reabrirProtocolo: ReabrirProtocoloUseCase;
    readonly transferirProtocolo: TransferirProtocoloUseCase;
    readonly listProtocoloAnexos: ListProtocoloAnexosUseCase;
    readonly uploadProtocoloAnexo: UploadProtocoloAnexoUseCase;
    // admin-gaps2 (G.5.20 Bolsa + NFS-e + Cobrança gateway)
    readonly solicitarBolsa: SolicitarBolsaUseCase;
    readonly aprovarBolsa: AprovarBolsaUseCase;
    readonly revogarBolsa: RevogarBolsaUseCase;
    readonly emitirNfse: EmitirNfseUseCase;
    readonly consultarNfse: ConsultarNfseUseCase;
    readonly emitirCobrancaGateway: EmitirCobrancaGatewayUseCase;
    readonly issueParcelaPix: IssueParcelaPixUseCase;
    readonly issueParcelaBoleto: IssueParcelaBoletoUseCase;
    // catalog-config (H.1 pedagógico novo)
    readonly listEducationLevels: ListEducationLevelsUseCase;
    readonly createEducationLevel: CreateEducationLevelUseCase;
    readonly updateEducationLevel: UpdateEducationLevelUseCase;
    readonly listKnowledgeAreas: ListKnowledgeAreasUseCase;
    readonly createKnowledgeArea: CreateKnowledgeAreaUseCase;
    readonly updateKnowledgeArea: UpdateKnowledgeAreaUseCase;
    readonly listTitulacoes: ListTitulacoesUseCase;
    readonly createTitulacao: CreateTitulacaoUseCase;
    readonly updateTitulacao: UpdateTitulacaoUseCase;
    readonly listRegrasRematricula: ListRegrasRematriculaUseCase;
    readonly createRegraRematricula: CreateRegraRematriculaUseCase;
    readonly updateRegraRematricula: UpdateRegraRematriculaUseCase;
    // H.2 pedagógico novo — Atividades + Fórum
    readonly listAssignments: ListAssignmentsUseCase;
    readonly createAssignment: CreateAssignmentUseCase;
    readonly updateAssignment: UpdateAssignmentUseCase;
    readonly publishAssignment: PublishAssignmentUseCase;
    readonly encerrarAssignment: EncerrarAssignmentUseCase;
    readonly arquivarAssignment: ArquivarAssignmentUseCase;
    readonly listAssignmentSubmissions: ListAssignmentSubmissionsUseCase;
    readonly corrigirSubmission: CorrigirSubmissionUseCase;
    readonly downloadSubmissionArquivo: DownloadSubmissionArquivoUseCase;
    readonly listForumTopicos: ListForumTopicosUseCase;
    readonly createForumTopico: CreateForumTopicoUseCase;
    readonly updateForumTopico: UpdateForumTopicoUseCase;
    readonly encerrarForumTopico: EncerrarForumTopicoUseCase;
    readonly arquivarForumTopico: ArquivarForumTopicoUseCase;
    readonly listForumRespostas: ListForumRespostasUseCase;
    readonly corrigirForumResposta: CorrigirForumRespostaUseCase;
    // H.3 pedagógico novo — Tutoria
    readonly listTutores: ListTutoresUseCase;
    readonly createTutor: CreateTutorUseCase;
    readonly updateTutor: UpdateTutorUseCase;
    readonly arquivarTutor: ArquivarTutorUseCase;
    readonly listAssuntos: ListAssuntosUseCase;
    readonly createAssunto: CreateAssuntoUseCase;
    readonly updateAssunto: UpdateAssuntoUseCase;
    readonly listSolicitacoes: ListSolicitacoesUseCase;
    readonly assumirSolicitacao: AssumirSolicitacaoUseCase;
    readonly listSolicitacaoMensagens: ListSolicitacaoMensagensUseCase;
    readonly responderSolicitacao: ResponderSolicitacaoUseCase;
    readonly encerrarSolicitacao: EncerrarSolicitacaoUseCase;
    // H.4 pedagógico novo — Comunicação
    readonly uploadCommunicationFile: UploadCommunicationFileUseCase;
    readonly downloadCommunicationFile: DownloadCommunicationFileUseCase;
    readonly listDisciplineResources: ListDisciplineResourcesUseCase;
    readonly addDisciplineResource: MutateDisciplineResourceUseCase;
    readonly reorderDisciplineResource: MutateDisciplineResourceUseCase;
    readonly removeDisciplineResource: MutateDisciplineResourceUseCase;
    readonly listAulasAoVivo: ListAulasAoVivoUseCase;
    readonly createAulaAoVivo: CreateAulaAoVivoUseCase;
    readonly updateAulaAoVivo: UpdateAulaAoVivoUseCase;
    readonly arquivarAulaAoVivo: ArquivarAulaAoVivoUseCase;
    readonly listMateriais: ListMateriaisUseCase;
    readonly createMaterial: CreateMaterialUseCase;
    readonly updateMaterial: UpdateMaterialUseCase;
    readonly arquivarMaterial: ArquivarMaterialUseCase;
    readonly listEbooks: ListEbooksUseCase;
    readonly createEbook: CreateEbookUseCase;
    readonly updateEbook: UpdateEbookUseCase;
    readonly arquivarEbook: ArquivarEbookUseCase;
    readonly baixarEbook: BaixarEbookUseCase;
    readonly listVideoaulas: ListVideoaulasUseCase;
    readonly createVideoaula: CreateVideoaulaUseCase;
    readonly updateVideoaula: UpdateVideoaulaUseCase;
    readonly arquivarVideoaula: ArquivarVideoaulaUseCase;
    readonly listAmbientacoes: ListAmbientacoesUseCase;
    readonly createAmbientacao: CreateAmbientacaoUseCase;
    readonly updateAmbientacao: UpdateAmbientacaoUseCase;
    readonly arquivarAmbientacao: ArquivarAmbientacaoUseCase;
    readonly listOutrosConteudos: ListOutrosConteudosUseCase;
    readonly createOutroConteudo: CreateOutroConteudoUseCase;
    readonly updateOutroConteudo: UpdateOutroConteudoUseCase;
    readonly arquivarOutroConteudo: ArquivarOutroConteudoUseCase;
    readonly listUnidadeItens: ListUnidadeItensUseCase;
    readonly createUnidadeItem: CreateUnidadeItemUseCase;
    readonly createUnidadeItemPdf: CreateUnidadeItemPdfUseCase;
    readonly createUnidadeItemDaBiblioteca: CreateUnidadeItemDaBibliotecaUseCase;
    readonly arquivarUnidadeItem: ArquivarUnidadeItemUseCase;
    readonly reordenarUnidadeItens: ReordenarUnidadeItensUseCase;
    readonly baixarUnidadeItem: BaixarUnidadeItemUseCase;
    readonly listManuais: ListManuaisUseCase;
    readonly createManual: CreateManualUseCase;
    readonly updateManual: UpdateManualUseCase;
    readonly arquivarManual: ArquivarManualUseCase;
    readonly enviarMensagem: EnviarMensagemUseCase;
    readonly listMensagensByAluno: ListMensagensByAlunoUseCase;
    // S1 Fundação
    readonly listAgenciadores: ListAgenciadoresUseCase;
    readonly createAgenciador: CreateAgenciadorUseCase;
    readonly updateAgenciador: UpdateAgenciadorUseCase;
    readonly removeAgenciador: RemoveAgenciadorUseCase;
    readonly listEmailTemplates: ListEmailTemplatesUseCase;
    readonly createEmailTemplate: CreateEmailTemplateUseCase;
    readonly updateEmailTemplate: UpdateEmailTemplateUseCase;
    readonly removeEmailTemplate: RemoveEmailTemplateUseCase;
    readonly previewEmailTemplate: PreviewEmailTemplateUseCase;
    readonly listIntegracoes: ListIntegracoesUseCase;
    readonly createIntegracao: CreateIntegracaoUseCase;
    readonly updateIntegracao: UpdateIntegracaoUseCase;
    readonly removeIntegracao: RemoveIntegracaoUseCase;
    readonly getTenantFinancialConfig: GetTenantFinancialConfigUseCase;
    readonly saveTenantFinancialConfig: SaveTenantFinancialConfigUseCase;
    // S2 Comercial
    readonly listGruposVenda: ListGruposVendaUseCase;
    readonly createGrupoVenda: CreateGrupoVendaUseCase;
    readonly updateGrupoVenda: UpdateGrupoVendaUseCase;
    readonly removeGrupoVenda: RemoveGrupoVendaUseCase;
    readonly listLinksCaptacao: ListLinksCaptacaoUseCase;
    readonly createLinkCaptacao: CreateLinkCaptacaoUseCase;
    readonly updateLinkCaptacao: UpdateLinkCaptacaoUseCase;
    readonly removeLinkCaptacao: RemoveLinkCaptacaoUseCase;
    readonly listProcessosSeletivos: ListProcessosSeletivosUseCase;
    readonly createProcessoSeletivo: CreateProcessoSeletivoUseCase;
    readonly updateProcessoSeletivo: UpdateProcessoSeletivoUseCase;
    readonly abrirProcessoSeletivo: AbrirProcessoSeletivoUseCase;
    readonly encerrarProcessoSeletivo: EncerrarProcessoSeletivoUseCase;
    readonly removeProcessoSeletivo: RemoveProcessoSeletivoUseCase;
    readonly listInscricoes: ListInscricoesUseCase;
    readonly criarInscricao: CriarInscricaoUseCase;
    readonly aprovarInscricao: AprovarInscricaoUseCase;
    readonly reprovarInscricao: ReprovarInscricaoUseCase;
    // S3 Acadêmico
    readonly listNoticias: ListNoticiasUseCase;
    readonly createNoticia: CreateNoticiaUseCase;
    readonly updateNoticia: UpdateNoticiaUseCase;
    readonly removeNoticia: RemoveNoticiaUseCase;
    readonly getNotasMultidisciplinar: GetNotasMultidisciplinarUseCase;
    readonly getCpaRelatorioAnalitico: GetCpaRelatorioAnaliticoUseCase;
    // S4 Relatórios
    readonly getRelatorioInadimplentes: GetRelatorioInadimplentesUseCase;
    readonly getRelatorioAdimplentes: GetRelatorioAdimplentesUseCase;
    readonly getRelatorioPagamentosPorPolo: GetRelatorioPagamentosPorPoloUseCase;
    readonly getRelatorioAcessoAva: GetRelatorioAcessoAvaUseCase;
    // S5 Livro de registros
    readonly listLivrosRegistro: ListLivrosRegistroUseCase;
    readonly abrirLivroRegistro: AbrirLivroRegistroUseCase;
    readonly fecharLivroRegistro: FecharLivroRegistroUseCase;
    readonly listLivroRegistroItens: ListLivroRegistroItensUseCase;
    readonly addLivroRegistroItem: AddLivroRegistroItemUseCase;
  };
}

export interface ContainerHooks {
  getAccessToken: () => string | null;
  refresh: () => Promise<string | null>;
  setSession: (s: AuthSession) => void;
  clearSession: () => void;
  getRefreshToken: () => string | null;
}

/**
 * Cria container de DI. Hooks são fornecidos pelo authStore (Pinia) —
 * mantém a inversão: infra depende de abstrações (callbacks) do presentation,
 * presentation depende de use cases via interface.
 */
export function buildContainer(baseURL: string, hooks: ContainerHooks): Container {
  const httpClient = createHttpClient({
    baseURL,
    getAccessToken: hooks.getAccessToken,
    onUnauthorized: hooks.refresh,
  });

  const authGateway: AuthGateway = new HttpAuthGateway(baseURL, httpClient);
  const tenantFeatureGateway: TenantFeatureGateway = new HttpTenantFeatureGateway(httpClient);
  const iesGateway: IesGateway = new HttpIesGateway(httpClient);
  const engajamentoGateway: EngajamentoGateway = new HttpEngajamentoGateway(httpClient);
  const commerce = buildCommerceSlice(httpClient);
  const crm = buildCrmSlice(httpClient);
  const content = buildContentSlice(httpClient);
  const organization = buildOrganizationSlice(httpClient);
  const identity = buildIdentitySlice(httpClient, baseURL);
  const enrollmentAdmin = buildEnrollmentAdminSlice(httpClient);
  const commerceGaps = buildCommerceGapsSlice(httpClient);
  const academy = buildAcademySlice(httpClient);
  const aluno = buildAlunoSlice(httpClient);
  const perfilAluno = buildPerfilAlunoSlice(httpClient);
  const acadadmin = buildAcadadminSlice(httpClient);
  const acadadminGaps = buildAcadadminGapsSlice(httpClient);
  const adminGaps2 = buildAdminGaps2Slice(httpClient);
  const catalogConfig = buildCatalogConfigSlice(httpClient);
  const pedagogico = buildPedagogicoSlice(httpClient);
  const tutoria = buildTutoriaSlice(httpClient);
  const composition = buildDisciplineCompositionSlice(httpClient);
  const comunicacao = buildComunicacaoSlice(httpClient);
  const ebook = buildEbookSlice(httpClient);
  const videoaula = buildVideoaulaSlice(httpClient);
  const ambientacao = buildAmbientacaoSlice(httpClient);
  const outroConteudo = buildOutroConteudoSlice(httpClient);
  const unidadeItem = buildUnidadeItemSlice(httpClient);
  const fundacao = buildFundacaoSlice(httpClient);
  const comercialGaps = buildComercialGapsSlice(httpClient);
  const acadGaps = buildAcadGapsSlice(httpClient);
  const relatorios = buildRelatoriosSlice(httpClient);
  const livroRegistro = buildLivroRegistroSlice(httpClient);
  const conteudoExterno = buildConteudoExternoSlice(httpClient);
  const {
    cohortGateway, catalogReadGateway, courseGateway, disciplineGateway,
    curriculumGateway, matriculaGateway, contratoGateway, parcelaGateway,
  } = academy.gateways;
  const { alunoGateway, racaGateway, cepGateway } = aluno.gateways;
  const {
    documentoGateway, requerimentoGateway, protocoloGateway, observacaoAlunoGateway,
    alunoStatusGateway, alunoBloqueioAcessoGateway, alunoVisao360Gateway,
    notaGateway, boletimGateway, frequenciaGateway,
  } = perfilAluno.gateways;
  const sessionStorage: SessionStorageGateway = new LocalSessionStorage();

  return {
    authGateway,
    alunoGateway,
    racaGateway,
    cepGateway,
    documentoGateway,
    requerimentoGateway,
    protocoloGateway,
    observacaoAlunoGateway,
    tenantFeatureGateway,
    alunoStatusGateway,
    alunoBloqueioAcessoGateway,
    alunoVisao360Gateway,
    notaGateway,
    boletimGateway,
    frequenciaGateway,
    cohortGateway,
    catalogReadGateway,
    courseGateway,
    iesGateway,
    disciplineGateway,
    curriculumGateway,
    matriculaGateway,
    contratoGateway,
    parcelaGateway,
    sessionStorage,
    useCases: {
      login: new LoginUseCaseImpl(authGateway, sessionStorage, hooks.setSession),
      logout: new LogoutUseCaseImpl(
        authGateway,
        sessionStorage,
        hooks.clearSession,
        hooks.getRefreshToken,
      ),
      forgot: new ForgotPasswordUseCaseImpl(authGateway),
      reset: new ResetPasswordUseCaseImpl(authGateway),
      ...aluno.useCases,
      ...perfilAluno.useCases,
      listTenantFeatures: new ListTenantFeaturesUseCaseImpl(tenantFeatureGateway),
      toggleFeature: new ToggleFeatureUseCaseImpl(tenantFeatureGateway),
      applyProfile: new ApplyProfileUseCaseImpl(tenantFeatureGateway),
      getMatriculaDisciplines: new GetMatriculaDisciplines(disciplineGateway),
      getEngajamento: new GetEngajamento(engajamentoGateway),
      ...crm.useCases,
      ...content.useCases,
      ...organization.useCases,
      ...identity.useCases,
      ...enrollmentAdmin.useCases,
      ...commerceGaps.useCases,
      ...commerce.useCases,
      getCurrentIes: new GetCurrentIesUseCaseImpl(iesGateway),
      ...academy.useCases,
      ...acadadmin.useCases,
      ...acadadminGaps.useCases,
      ...adminGaps2.useCases,
      ...catalogConfig.useCases,
      ...pedagogico.useCases,
      ...tutoria.useCases,
      ...composition.useCases,
      ...comunicacao.useCases,
      ...ebook.useCases,
      ...videoaula.useCases,
      ...ambientacao.useCases,
      ...outroConteudo.useCases,
      ...unidadeItem.useCases,
      ...fundacao.useCases,
      ...comercialGaps.useCases,
      ...acadGaps.useCases,
      ...relatorios.useCases,
      ...livroRegistro.useCases,
      ...conteudoExterno.useCases,
    },
  };
}
